export const MAX_SOURCE_BYTES = 3 * 1024 * 1024;
export const MAX_CHUNK_BYTES = 96 * 1024;

const MIN_CHUNK_BYTES = 32 * 1024;
const BOUNDARY_MASK = 0x0f;
const encoder = new TextEncoder();

export async function describeSourceChunks(source) {
  const parts = chunkSource(source);
  const chunks = await Promise.all(
    parts.map(async (content) => ({
      hash: await sha256(content),
      content,
      byteLength: encoder.encode(content).byteLength
    }))
  );

  return {
    sourceSize: chunks.reduce((total, chunk) => total + chunk.byteLength, 0),
    chunkHashes: chunks.map((chunk) => chunk.hash),
    chunks
  };
}

export function chunksMissingFrom(description, knownHashes = []) {
  const known = new Set(knownHashes);
  const included = new Set();
  return description.chunks
    .filter((chunk) => {
      if (known.has(chunk.hash) || included.has(chunk.hash)) return false;
      included.add(chunk.hash);
      return true;
    })
    .map(({ hash, content }) => ({ hash, content }));
}

export async function sha256(value) {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(value));
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function chunkSource(source) {
  if (source.length === 0) return [""];

  const lines = source.match(/[^\n]*\n|[^\n]+$/g) ?? [source];
  const chunks = [];
  let current = "";
  let currentBytes = 0;

  const flush = () => {
    if (!current) return;
    chunks.push(current);
    current = "";
    currentBytes = 0;
  };

  for (const line of lines) {
    for (const segment of splitByBytes(line, MAX_CHUNK_BYTES)) {
      const segmentBytes = encoder.encode(segment).byteLength;
      if (current && currentBytes + segmentBytes > MAX_CHUNK_BYTES) flush();

      current += segment;
      currentBytes += segmentBytes;

      if (
        currentBytes >= MAX_CHUNK_BYTES ||
        (currentBytes >= MIN_CHUNK_BYTES && (stableLineHash(segment) & BOUNDARY_MASK) === 0)
      ) {
        flush();
      }
    }
  }

  flush();
  return chunks.length ? chunks : [""];
}

function splitByBytes(value, maxBytes) {
  if (encoder.encode(value).byteLength <= maxBytes) return [value];

  const parts = [];
  let start = 0;
  while (start < value.length) {
    let low = start + 1;
    let high = Math.min(value.length, start + maxBytes);
    let end = low;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const safeMiddle = avoidBrokenSurrogate(value, middle);
      const size = encoder.encode(value.slice(start, safeMiddle)).byteLength;
      if (size <= maxBytes) {
        end = safeMiddle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    if (end <= start) {
      end = avoidBrokenSurrogate(value, start + 1);
    }
    parts.push(value.slice(start, end));
    start = end;
  }
  return parts;
}

function avoidBrokenSurrogate(value, index) {
  if (
    index > 0 &&
    index < value.length &&
    /[\uD800-\uDBFF]/.test(value[index - 1]) &&
    /[\uDC00-\uDFFF]/.test(value[index])
  ) {
    return index - 1;
  }
  return index;
}

function stableLineHash(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
